import {createStore, filterNil} from '@ngneat/elf';
import {getEntityByPredicate, selectEntity, withEntities} from "@ngneat/elf-entities";
import {Injectable} from "@angular/core";
import {FeilhåndteringArgumenter, hentFraLokallagerEllerNull, registrerEntitet, serverFraLagerOgRequestPakketIMeta, slettAlleEntiteterMedForeldre, somFeilhåndteringsstrategiForDataOgMeta, stdFeilhåndteringsstrategi, stdFeilhåndteringsstrategiForDataOgMeta, STRATEGI_TYPE, visFeilmeldingVhjaMeldingTilBruker} from "felles-ng2/tjenester/datalager/datatjeneste";
import {initDebugger} from "felles-ng2/støttefunksjoner/debug";
import {firstValueFrom, Observable} from "rxjs";
import {ModelloppdateringType, MøteId, OppdateringmodellSignatarSignert, ProtokollførtMøtedeltakereModell, ProtokollRessursClient} from "felles-ng2/tjenester/ressurser/generertNg2HttpKlient";
import {DataOgMeta, kunRequestRespons, mapTilData} from "felles-ng2/tjenester/datalager/DataOgMeta";
import {MeldingTilBrukerNg2} from "felles-ng2/tjenester/MeldingTilBrukerNg2";
import {Modelloppdateringer} from "felles-ng2/tjenester/Modelloppdateringer";
import {DatalagerAbonnenter} from "felles-ng2/tjenester/datalager/DatalagerAbonnenter";
import {distinctUntilChangedDeep} from "felles-ng2/støttefunksjoner/observable";
import {HttpResponse} from "@angular/common/http";
import {ModellOpprettetEllerOppdatertHendelseHåndteringBygger} from "felles-ng2/tjenester/datalager/modellHendelseHåndtering/ModellOpprettetEllerOppdatertHendelseHåndteringBygger";
import {interneProtokollførteMøtedeltakere} from "felles-ng2/modell/protokollModellHjelpere";
import {nil} from "felles-ng2/typer/nullish";
import {erIkkeNil} from "felles-ng2/støttefunksjoner/nilsjekk";

const debug = initDebugger('ProtokollførtMøtedeltakereDatatjeneste');

/** Datalager for {@link ProtokollførtMøtedeltakereModell}. Bruker {@link MøteId} som forelder ( __forelderId = møteId ).
 * Merk at modellen inneholder lister med deltakere, ikke enkeltdeltakere. */
@Injectable() //tilgjengeliggjøres via DatalagerModul
export class ProtokollførtMøtedeltakereDatatjeneste {

    /** Lager for {@link ProtokollførtMøtedeltakereModell}.
     * Merk at vi bruker møteId som id for modellen i lageret (siden det kun finnes én ProtokollførtMøtedeltakereModell per møte) */
    readonly protokollførtMøtedeltakereLager = createStore({name: 'protokollførtMøtedeltakere'}, withEntities<ProtokollførtMøtedeltakereModell, 'moeteId'>({idKey: 'moeteId'}));

    /** Holder rede på åpne subscriptions basert på møteId. Benyttes ifbm modelloppdateringer. */
    readonly møteAbonnenter = new DatalagerAbonnenter<MøteId>('protokollførtMøtedeltakere.moeteId'); //public for testbarhet

    constructor(private protokollRessurs: ProtokollRessursClient,
                private meldingTilBruker: MeldingTilBrukerNg2,
                private modelloppdateringer: Modelloppdateringer) {
        this.lyttTilModelloppdateringer();
    }

    /** Returnerer angitt ProtokollførtMøtedeltakereModell fra lokallager, eller null dersom ikke funnet */
    hentFraLokallager(møteId: MøteId): ProtokollførtMøtedeltakereModell | null {
        return hentFraLokallagerEllerNull(this.protokollførtMøtedeltakereLager, møteId);
    }

    /** Returnerer observerbar som leverer ProtokollførtMøtedeltakereModell knyttet til angitt møte fra lokallager.
     * Leverer også når det skjer endringer knyttet til angitt møte i lokallager */
    finnTilknyttetMøteFraLokallager(møteId: MøteId): Observable<ProtokollførtMøtedeltakereModell> {
        debug('finnTilknyttetMøteFraLokallager', møteId, this.møteAbonnenter.antallAbonnenter(møteId))
        return this.møteAbonnenter.observer(møteId,
          this.protokollførtMøtedeltakereLager.pipe(
            selectEntity(møteId),
            filterNil(),
            distinctUntilChangedDeep() //kun lever verdier fra lageret ved reelle endringer
          ));
    }

    /** Laster ProtokollførtMøtedeltakereModell for angitt møte fra server og oppdaterer lokalt datalager med disse. */
    async relast(møteId: MøteId, feilhåndteringsstrategi = stdFeilhåndteringsstrategiForDataOgMeta): Promise<void> {
        await firstValueFrom(this.hentMedMeta(møteId, feilhåndteringsstrategi).pipe(kunRequestRespons()));
    }

    hentMedMeta(møteId: MøteId, feilhåndteringsstrategi = stdFeilhåndteringsstrategiForDataOgMeta): Observable<DataOgMeta<ProtokollførtMøtedeltakereModell>> {
        return this.møteAbonnenter.observer(møteId,
          serverFraLagerOgRequestPakketIMeta({
              ressurskall: () => this.protokollRessurs.deltakere(møteId),
              registrerILager: (respons: HttpResponse<ProtokollførtMøtedeltakereModell>) => this.erstattDeltakereTilknyttetMøteILokallager(respons.body!),
              hentFraLager: () => this.protokollførtMøtedeltakereLager.pipe(
                selectEntity(møteId),
                filterNil(),
                distinctUntilChangedDeep() //kun lever verdier fra lageret ved reelle endringer
              ),
              ...this.feilhåndteringArgumenter(feilhåndteringsstrategi, 'Kunne ikke hente protokollførte møtedeltakere', 'hentMedMeta')
          }));
    }

    hent(møteId: MøteId, feilhåndteringsstrategi = stdFeilhåndteringsstrategi): Observable<ProtokollførtMøtedeltakereModell> {
        return this.hentMedMeta(møteId, somFeilhåndteringsstrategiForDataOgMeta(feilhåndteringsstrategi))
          .pipe(mapTilData());
    }

    /** Erstatter ProtokollførtMøtedeltakereModell knytte til angitt møteId i lokallager */
    erstattDeltakereTilknyttetMøteILokallager(deltakere: ProtokollførtMøtedeltakereModell): ProtokollførtMøtedeltakereModell {
        return registrerEntitet(this.protokollførtMøtedeltakereLager, deltakere);
    }

    /** Sletter ProtokollførtMøtedeltakereModell tilknyttet angitte møter fra lokallageret */
    slettDeltakereTilknyttetMøterILokallager(møteIder: MøteId[]): void {
        debug('slettDeltakereTilknyttetMøterILokallager', møteIder);
        slettAlleEntiteterMedForeldre(this.protokollførtMøtedeltakereLager, møteIder);
    }

    //hjelpemetoder

    private feilhåndteringArgumenter<RET_TYPE, STRATEGI extends STRATEGI_TYPE>(
      feilhåndteringsstrategi: STRATEGI,
      brukerrettetFeilmelding: string,
      metodeNavnForDebug: string): FeilhåndteringArgumenter<RET_TYPE, STRATEGI> {
        return {
            feilhåndteringsstrategi,
            visFeilmelding: visFeilmeldingVhjaMeldingTilBruker(brukerrettetFeilmelding, this.meldingTilBruker),
            dataReturnertVedFeil: null,
            debug: (formatter: any, ...args: any[]): void => debug(`${metodeNavnForDebug}: ${formatter}`, args),
        }
    }

    /**
     * Lytt til relevante modelloppdateringer via {@link Modelloppdateringer} (push-baserte oppdateringer).
     * Merk at vi aldri avregistrerer subscription vi oppretter her da data-tjenesten aldri termineres.
     */
    private lyttTilModelloppdateringer() {
        this.lyttTilSignatarSignert();
    }

    private lyttTilSignatarSignert() {
        new ModellOpprettetEllerOppdatertHendelseHåndteringBygger<OppdateringmodellSignatarSignert, ProtokollførtMøtedeltakereModell, 'moeteId'>(this.protokollførtMøtedeltakereLager, this.modelloppdateringer)
          .lyttTil(ModelloppdateringType.SIGNATAR_SIGNERT)
          .medFilter(hendelse => erIkkeNil(this.finnLagerinnslagKnyttetTilSignatar(hendelse.modell)))
          //når entitetId oppslaget skjer har filtermetoden sikret at lageret faktisk inneholder et innslag knyttet til signatarId
          .medEntitetId(hendelseModell => this.finnMøteIdFraLagerinnslagKnyttetTilSignatar(hendelseModell)!)
          .medEntitetLaster(hendelseModell => this.protokollRessurs.deltakere(this.finnMøteIdFraLagerinnslagKnyttetTilSignatar(hendelseModell)!).råRespons())
          .medAbonnentoppslag(this.møteAbonnenter, hendelseModell => this.finnMøteIdFraLagerinnslagKnyttetTilSignatar(hendelseModell)!)
          .oppdaterLagerVedHendelse();
    }

    private finnLagerinnslagKnyttetTilSignatar(hendelseModell: OppdateringmodellSignatarSignert): ProtokollførtMøtedeltakereModell | nil {
        return this.protokollførtMøtedeltakereLager.query(
          getEntityByPredicate(deltakerer =>
            interneProtokollførteMøtedeltakere(deltakerer).some(deltaker => deltaker.signatar?.id === hendelseModell.signatarId))
        );
    }

    private finnMøteIdFraLagerinnslagKnyttetTilSignatar(hendelseModell: OppdateringmodellSignatarSignert): MøteId | null {
        return this.finnLagerinnslagKnyttetTilSignatar(hendelseModell)?.moeteId ?? null;
    }

}
